import { useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import BookingForm from '@/components/BookingForm';
import { CheckCircle } from 'lucide-react';
import { useLanguage } from '@/hooks/useLanguage';
import SEO from '@/components/SEO';
import ScrollToTop from '@/components/ScrollToTop';

const Booking = () => {
  const { t } = useLanguage();
  const [searchParams] = useSearchParams();
  const preselectedService = searchParams.get('service');

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const benefits = [
    t('consult.benefit1'),
    t('consult.benefit2'),
    t('consult.benefit3'),
    t('consult.benefit4'),
    t('consult.benefit5'),
    t('consult.benefit6'),
  ];

  return (
    <>
      <SEO 
        title={t('booking.title')}
        description={t('booking.subtitle')}
      />
      <ScrollToTop />
      <div className="min-h-screen flex flex-col bg-background">
        <Header />
        
        <main className="flex-1">
          {/* Hero Section */}
          <section className="relative py-20 bg-gradient-hero text-primary-foreground">
            <div className="container mx-auto px-4">
              <div className="max-w-4xl mx-auto text-center">
                <h1 className="text-4xl md:text-5xl font-bold mb-6">
                  {t('booking.title')}
                </h1>
                <p className="text-xl opacity-95 mb-4">
                  {t('booking.subtitle')}
                </p>
              </div>
            </div>
          </section>

          {/* Booking Section */}
          <section className="py-20 bg-background">
            <div className="container mx-auto px-4">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 max-w-6xl mx-auto">
                {/* Benefits Column */}
                <div>
                  <h2 className="text-3xl font-bold mb-6">{t('consult.expect')}</h2>
                  <p className="text-lg text-muted-foreground mb-8">
                    {t('consult.expect.desc')}
                  </p>

                  <h3 className="text-xl font-bold mb-4">{t('consult.during')}</h3>
                  <ul className="space-y-4 mb-8">
                    {benefits.map((benefit, index) => (
                      <li key={index} className="flex items-start gap-3">
                        <CheckCircle className="h-6 w-6 text-accent flex-shrink-0 mt-0.5" />
                        <span className="text-muted-foreground">{benefit}</span>
                      </li>
                    ))}
                  </ul>

                  <div className="bg-card p-6 rounded-lg shadow-md border-l-4 border-accent">
                    <h3 className="text-lg font-bold mb-2">{t('consult.note')}</h3>
                    <p className="text-sm text-muted-foreground">
                      {t('consult.note.desc')}
                    </p>
                  </div>

                  <div className="mt-8 bg-primary/5 border border-primary/20 p-6 rounded-lg">
                    <h4 className="font-bold mb-2">{t('contact.office')}</h4>
                    <p className="text-sm text-muted-foreground mb-4">
                      10925 Beechnut St A106<br />
                      Houston, TX 77072
                    </p>
                    <h4 className="font-bold mb-2">{t('contact.phone')}</h4>
                    <p className="text-sm text-muted-foreground">
                      <strong>ENGLISH:</strong> <a href="tel:8329303059" className="text-primary hover:underline">(832) 930-3059</a><br />
                      <strong>ESPAÑOL:</strong> <a href="tel:8323567254" className="text-primary hover:underline">(832) 356-7254</a><br />
                      <strong>TIẾNG VIỆT:</strong> <a href="tel:8329303059" className="text-primary hover:underline">(832) 930-3059</a>
                    </p>
                  </div>
                </div>

                {/* Booking Form Column */}
                <div>
                  <BookingForm preselectedService={preselectedService} />
                </div>
              </div>
            </div>
          </section>
        </main>

        <Footer />
      </div>
    </>
  );
};

export default Booking;
